import { useCallback, useEffect, useState } from 'react';

interface IntersectionObserverProps {
  /**
   * Use useCallback or useMemo for callback
   */
  callback: IntersectionObserverCallback;
  options?: IntersectionObserverInit & { disabled?: boolean };
}

/**
 * Allows you to observe the intersection of an element with the viewport.
 * In simpler words, it allows you to know when an element is visible in the viewport.
 * @param {object} options - The options for the useIntersectionObserver.
 * @param {IntersectionObserverCallback} options.callback - The callback function to be called when an element is visible in the viewport.
 * @param {IntersectionObserverInit} options.options - The options for the IntersectionObserver.
 * @returns {Function} A function that takes a DOM node as an argument and observes the intersection of that node with the viewport.
 */
export function useIntersectionObserver({
  callback,
  options: { threshold = 0.01, root, rootMargin, disabled } = {}
}: IntersectionObserverProps) {
  const [observer, setObserver] = useState<IntersectionObserver>();

  useEffect(() => {
    let nextObserver: IntersectionObserver;
    if (!disabled) {
      nextObserver = new IntersectionObserver(callback, { threshold, root, rootMargin });
      setObserver((prev) => {
        if (prev) {
          prev.disconnect();
        }
        return nextObserver;
      });
    }

    return () => {
      nextObserver?.disconnect();
    };
  }, [callback, threshold, root, rootMargin, disabled]);

  return useCallback(
    (node: HTMLElement | null) => {
      if (!observer) {
        return;
      }
      if (!node) {
        observer?.disconnect();
      } else {
        observer?.observe(node);
      }
    },
    [observer]
  );
}

export function onVisibleInViewPort<T extends HTMLElement>(element: T, callback: (element: T) => void) {
  new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0) {
        callback(element);
        observer.disconnect();
      }
    });
  }).observe(element);
}
